// pages/rank-predictor.js
import Head from 'next/head';
import RankPredictor from '@/components/RankPredictor';

const PAGE_URL = 'https://neetsupport.com/neet-rank-predictor';
const IMAGE_URL =
  'https://neetsupport.com/assets/img/home/NeetPredict/NEETPredictor.webp';
const SITE_URL = 'https://neetsupport.com';
const SITE_NAME = 'NEET Support';

export default function RankPredictorPage() {
  const title =
    'NEET Rank Predictor 2026 – Predict Rank by Marks Free | NEET Support';

  // ✅ SHORT + HIGH CTR (≈155 chars)
  const description =
    'Use our free NEET Rank Predictor 2026 to estimate your NEET UG rank by marks. Check marks vs rank trends, cut-off insights, and plan your counselling.';

  // ✅ BEST KEYWORDS (from your SEO report)
  const keywords =
    'neet rank predictor 2026, neet rank predictor, rank predictor, neet ug rank predictor, predict neet rank by marks, neet marks vs rank 2026, neet score calculator, nta rank predictor, neet rank estimator, neet cutoff 2026, neet cut off, neet college predictor, free neet rank predictor, college predictor, neet pg rank predictor, neet pg marks vs rank';

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'NEET Rank Predictor 2026',
    url: PAGE_URL,
    description,
    inLanguage: 'en-IN',
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: IMAGE_URL
    },
    about: [
      'NEET Rank Predictor 2026',
      'NEET UG rank predictor',
      'NEET marks vs rank 2026',
      'NEET score calculator',
      'NEET cut off',
      'College predictor'
    ],
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://neetsupport.com/favicon/neetsupport-logo.webp'
      }
    }
  };

  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'NEET Rank Predictor 2026',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: PAGE_URL,
    image: IMAGE_URL,
    description,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR'
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'NEET Rank Predictor 2026: What score is required to qualify NEET 2026?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The score required to qualify NEET 2026 depends on the official cutoff released after results. The rank predictor only provides an estimated rank based on marks.'
        }
      },
      {
        '@type': 'Question',
        name: 'How does the NEET Rank Predictor 2026 work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'It uses previous year marks vs rank data to estimate your expected NEET rank based on your marks.'
        }
      },
      {
        '@type': 'Question',
        name: 'Is the NEET Rank Predictor 2026 free?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, it is completely free and provides instant rank estimation.'
        }
      },
      {
        '@type': 'Question',
        name: 'Can I use this rank for college prediction?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, you can use your predicted rank along with a college predictor to shortlist medical colleges.'
        }
      }
    ]
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'NEET Rank Predictor 2026',
        item: PAGE_URL
      }
    ]
  };

  return (
    <>
      <Head>
        {/* ✅ PRIMARY SEO */}
        <title>{title}</title>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta
          name="robots"
          content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        />
        <link rel="canonical" href={PAGE_URL} />

        {/* ✅ BRANDING */}
        <meta name="theme-color" content="#ffffff" />

        {/* ✅ OPEN GRAPH */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta
          property="og:image:alt"
          content="NEET Rank Predictor 2026 by NEET Support"
        />

        {/* ✅ TWITTER */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={IMAGE_URL} />
        <meta
          name="twitter:image:alt"
          content="NEET Rank Predictor 2026 by NEET Support"
        />

        {/* ✅ STRUCTURED DATA */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(webPageSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(softwareApplicationSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema)
          }}
        />
      </Head>

      <RankPredictor />
    </>
  );
}